import { motion } from "framer-motion";
import { PackageCheck, Truck, RotateCcw, Receipt, Boxes, ShieldCheck } from "lucide-react";

const DATA_IMG =
  "https://images.unsplash.com/photo-1727434032773-af3cd98375ba?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGRhdGElMjBnbG93aW5nfGVufDB8fHx8MTc4NTA0NzgwNHww&ixlib=rb-4.1.0&q=85";

const CARDS = [
  {
    icon: PackageCheck,
    title: "Order Management",
    body: "Mark-as-shipped is guarded by strict inventory validation. Insufficient stock becomes a Quantity Problem — never a silent oversell.",
    span: "md:col-span-5 lg:col-span-7",
  },
  {
    icon: Truck,
    title: "Inbound Receiving",
    body: "Partial receiving per SKU with live progress bars. Damaged units are quarantined — never added to sellable stock.",
    span: "md:col-span-3 lg:col-span-5",
  },
  {
    icon: RotateCcw,
    title: "Returns Auto-Restock",
    body: "Confirm each line as Sellable or Non-Sellable. Sellable quantities restock automatically; the rest is quarantined with photos attached.",
    span: "md:col-span-4 lg:col-span-4",
  },
  {
    icon: Boxes,
    title: "Audited Inventory",
    body: "Every adjustment demands a reason and writes an immutable transaction. Stock counts you can defend.",
    span: "md:col-span-4 lg:col-span-4",
  },
  {
    icon: Receipt,
    title: "Invoicing Summaries",
    body: "Per-store billing periods: shipped, returned, cancelled, problems — exported to CSV in one click.",
    span: "md:col-span-4 lg:col-span-4",
  },
];

const cardMotion = {
  hidden: { opacity: 0, y: 32 },
  show: (i) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.08, ease: "easeOut" } }),
};

export const FeaturesBento = () => (
  <section id="features" className="relative py-24 lg:py-32" data-testid="features-section">
    <div className="max-w-7xl mx-auto px-5 lg:px-10">
      <p className="text-xs md:text-sm uppercase tracking-[0.3em] text-blue-600 font-bold mb-4">Core Platform</p>
      <h2 className="lp-display max-w-3xl">
        <span className="block text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-[#0b1530] mb-4">
          Every flow, engineered for accountability.
        </span>
        <span className="text-base md:text-lg font-medium text-slate-500">
          Built for 3PL operators who cannot afford a single lost unit.
        </span>
      </h2>

      <div className="mt-14 grid grid-cols-1 md:grid-cols-8 lg:grid-cols-12 gap-6">
        <motion.div
          variants={cardMotion} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} custom={0}
          className="md:col-span-8 lg:col-span-12 relative rounded-2xl overflow-hidden group hover:-translate-y-1 transition-transform duration-300 shadow-2xl shadow-blue-900/20"
          data-testid="feature-card-hero"
        >
          <img src={DATA_IMG} alt="Glowing data flow" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0b1530]/95 via-[#0b1530]/70 to-[#0b1530]/30" />
          <div className="relative p-8 lg:p-14 max-w-xl">
            <span className="inline-flex items-center justify-center h-[3.25rem] w-[3.25rem] rounded-2xl bg-white/10 border border-white/25 backdrop-blur-sm mb-6">
              <ShieldCheck className="text-sky-300" size={26} strokeWidth={1.5} />
            </span>
            <h3 className="lp-display text-2xl md:text-3xl font-semibold tracking-tight text-white">Strict Multi-Tenant Isolation</h3>
            <p className="mt-4 text-base text-slate-200 leading-relaxed">
              Every query is scoped to a warehouse. Every write is validated against the tenant boundary.
              Your clients' data never crosses the line — enforced at the API layer, not the UI.
            </p>
          </div>
        </motion.div>

        {CARDS.map((c, i) => (
          <motion.div
            key={c.title}
            variants={cardMotion} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} custom={i + 1}
            className={`${c.span} lp-card rounded-2xl border border-slate-200 p-8 lg:p-9 hover:-translate-y-1 hover:shadow-xl hover:shadow-blue-600/15 hover:border-blue-200 transition-all duration-300`}
            data-testid={`feature-card-${i}`}
          >
            <span className="lp-icon-chip mb-6"><c.icon className="text-blue-600" size={25} strokeWidth={1.5} /></span>
            <h3 className="lp-display text-xl md:text-2xl font-semibold tracking-tight text-[#0b1530]">{c.title}</h3>
            <p className="mt-3 text-[15px] md:text-base text-slate-600 leading-relaxed">{c.body}</p>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);
